import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';

// Layouts
import MainLayout from '@layouts/MainLayout';
import AuthLayout from '@layouts/AuthLayout';

// Pages
import Home from '@pages/Home';
import Login from '@pages/auth/Login';
import Register from '@pages/auth/Register';
import AppStoreCallback from '@pages/auth/AppStoreCallback';
import PlayStoreAuth from '@pages/auth/PlayStoreAuth';
import Dashboard from '@pages/dashboard';
import ProgrammationCreate from '@pages/programmation/CreateProgrammation';
import EditProgrammation from '@pages/programmation/EditProgrammation';
import ProgrammationView from '@pages/programmation/ViewProgrammation';
import ProgrammationPublic from '@pages/programmation/PublicProgrammation';
import Planification from './pages/Planification';
import AdminDashboard from '@pages/admin';
import TestAuth from '@pages/TestAuth';
import NotFound from '@pages/NotFound';
import ProfilePage from '@pages/profile';

// Hooks
import { useUser } from './hooks/useUser';
import { UserProvider } from './contexts/UserContext';
import { useAuthContext } from './contexts/AuthContext';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route Component
type ProtectedRouteProps = {
  children: React.ReactNode;
};

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, status } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('[PROTECTED ROUTE] Vérification accès:', { isAuthenticated, status });
  console.log('[PROTECTED ROUTE] Location:', location.pathname);

  useEffect(() => {
    if (!isAuthenticated && status !== 'loading') {
      console.log('[PROTECTED ROUTE] Redirection vers login (non authentifié)');
      navigate('/login', { replace: true, state: { from: location } });
    } else {
      console.log('[PROTECTED ROUTE] Accès autorisé (authentifié)');
    }
  }, [isAuthenticated, status, navigate, location]);

  if (status === 'loading') {
    return <div>Chargement...</div>;
  }

  // Toujours afficher les enfants, la redirection est gérée par useEffect
  return <>{children}</>;
}

// Public Only Route Component
function PublicOnlyRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, status } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  console.log('[PUBLIC ROUTE] Vérification accès:', { isAuthenticated, status });

  useEffect(() => {
    if (isAuthenticated) {
      console.log('[PUBLIC ROUTE] Redirection vers', from, '(déjà authentifié)');
      navigate(from, { replace: true });
    } else {
      console.log('[PUBLIC ROUTE] Accès autorisé (non authentifié)');
    }
  }, [isAuthenticated, navigate, from, status]);

  if (status === 'loading') {
    return <div>Chargement...</div>;
  }

  // Toujours afficher les enfants, la redirection est gérée par useEffect
  return <>{children}</>;
}

// Composant pour gérer la redirection automatique (doit être dans le Router)
function AutoRedirect() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (user && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/register')) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, location.pathname, navigate]);
  
  return null; // Ce composant ne rend rien
}

// Composant interne pour utiliser useAuthContext après que AuthProvider soit monté
function AuthenticatedApp() {
  const { user, isAuthenticated } = useAuthContext();
  
  console.log('[APP] AuthenticatedApp - isAuthenticated:', isAuthenticated);
  console.log('[APP] AuthenticatedApp - user Firebase:', user ? user.email : 'Absent');
  
  const { userDoc, loading, error } = useUser(user);
  
  return (
    <UserProvider value={{ user: userDoc, loading, error }}>
      <Router>
        <Box minHeight="100vh" bgcolor="background.default" display="flex" flexDirection="column" pt={7}>
          <Toaster position="top-center" toastOptions={{
            style: {
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '0.9rem',
            },
            duration: 3000,
          }} />
          <AutoRedirect />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Home />} />
              <Route path="programmation/:id" element={<ProgrammationPublic />} />
              <Route path="programmation/:id/public" element={<ProgrammationPublic />} />
              
              {/* Auth Routes - Only accessible when not logged in */}
              <Route element={<AuthLayout />}>
                <Route 
                  path="login" 
                  element={
                    <PublicOnlyRoute>
                      <Login />
                    </PublicOnlyRoute>
                  } 
                />
                <Route 
                  path="register" 
                  element={
                    <PublicOnlyRoute>
                      <Register />
                    </PublicOnlyRoute>
                  } 
                />
                <Route 
                  path="appstore-callback" 
                  element={<AppStoreCallback />} 
                />
                <Route 
                  path="playstore-auth" 
                  element={<PlayStoreAuth />} 
                />
              </Route>

              {/* Protected Routes - Only accessible when logged in */}
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="programmation/create"
                element={
                  <ProtectedRoute>
                    <ProgrammationCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="programmation/:id/edit"
                element={
                  <ProtectedRoute>
                    <EditProgrammation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="programmation/:id/view"
                element={
                  <ProtectedRoute>
                    <ProgrammationView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="planification"
                element={
                  <ProtectedRoute>
                    <Planification />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="profil"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Route>
            
            {/* Route de test d'authentification - Accessible uniquement en développement */}
            {process.env.NODE_ENV === 'development' && (
              <Route path="/test-auth" element={<TestAuth />} />
            )}
          </Routes>
        </Box>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </UserProvider>
  );
}

function App() {
  // Thème MUI personnalisé : thème clair et neutre
  const theme = createTheme({
    palette: {
      mode: 'light' as const,
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
        contrastText: '#fff',
      },
      secondary: {
        main: '#9c27b0',
        light: '#ba68c8',
        dark: '#7b1fa2',
        contrastText: '#fff',
      },
      background: {
        default: '#f5f5f5',
        paper: '#ffffff',
      },
      text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: 'rgba(0, 0, 0, 0.6)',
        disabled: 'rgba(0, 0, 0, 0.38)',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 500,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 500,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 500,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 500,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 400,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            padding: '8px 16px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            overflow: 'hidden',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            maxWidth: '180px',
          },
        },
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
